import remend, { type RemendHandler, type RemendOptions } from "remend";

// HTML-escape the input first, then apply a fixed set of safe transforms — the
// output can only contain tags we emit explicitly, so v-html consumers are safe.

const HTML_ENTITIES: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
};

const SAFE_URL_PATTERN = /^(https?:\/\/|mailto:|tel:|\/)/i;
const INTERNAL_PATH_PATTERN = /^\/(?!\/)/;

function escapeHtml(text: string): string {
    return text.replace(/[&<>"']/g, (character) => HTML_ENTITIES[character]);
}

function isSafeUrl(url: string): boolean {
    return SAFE_URL_PATTERN.test(url.trim());
}

function isInternalPath(url: string): boolean {
    return INTERNAL_PATH_PATTERN.test(url.trim());
}

function renderInline(text: string): string {
    let output = text;

    // Inline code first so its contents are immune to the other transforms below.
    output = output.replace(/`([^`]+)`/g, "<code>$1</code>");

    output = output.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
    output = output.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");

    output = output.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
    output = output.replace(
        /(^|[^a-zA-Z0-9_])_([^_\n]+)_(?![a-zA-Z0-9_])/g,
        "$1<em>$2</em>",
    );

    output = output.replace(
        /\[([^\]]+)\]\(([^)\s]+)\)/g,
        (_match, label: string, url: string): string => {
            if (!isSafeUrl(url)) {
                return label;
            }
            if (isInternalPath(url)) {
                return `<a class="internal-link" data-internal-path="${url}">${label}</a>`;
            }
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`;
        },
    );

    return output;
}

interface ListContext {
    ordered: boolean;
    indent: number;
    items: string[];
    nested: (ListContext | null)[];
}

type TableAlignment = "left" | "center" | "right" | null;

function renderListItems(list: ListContext): string {
    const tag = list.ordered ? "ol" : "ul";
    const items = list.items
        .map((item, index): string => {
            const nested = list.nested[index];
            const nestedHtml = nested === null ? "" : renderListItems(nested);
            return `<li>${renderInline(item)}${nestedHtml}</li>`;
        })
        .join("");
    return `<${tag}>${items}</${tag}>`;
}

function detectListItem(
    line: string,
): { indent: number; ordered: boolean; content: string } | null {
    const match = line.match(/^(\s*)([-*]|\d+\.)\s+(.*)$/);
    if (match === null) {
        return null;
    }
    return {
        indent: match[1].length,
        ordered: /^\d/.test(match[2]),
        content: match[3],
    };
}

function detectHeading(
    line: string,
): { level: number; content: string } | null {
    const match = line.match(/^(#{1,4})\s+(.+)$/);
    if (match === null) {
        return null;
    }
    return { level: match[1].length, content: match[2] };
}

function detectFenceOpen(line: string): { fence: string } | null {
    const match = line.match(/^(\s*)(`{3,}|~{3,})/);
    if (match === null) {
        return null;
    }
    return { fence: match[2][0].repeat(match[2].length) };
}

function isFenceClose(line: string, fence: string): boolean {
    const match = line.match(/^\s*(`{3,}|~{3,})\s*$/);
    if (match === null) {
        return false;
    }
    return match[1][0] === fence[0] && match[1].length >= fence.length;
}

function isHorizontalRule(line: string): boolean {
    return /^\s*([-*_])(\s*\1){2,}\s*$/.test(line);
}

function parseAlignmentRow(line: string): TableAlignment[] | null {
    const trimmed = line.trim();
    if (!/^\|?\s*:?-+:?(\s*\|\s*:?-+:?)+\s*\|?$/.test(trimmed)) {
        return null;
    }
    return splitTableRow(trimmed).map((cell): TableAlignment => {
        const value = cell.trim();
        const startsWithColon = value.startsWith(":");
        const endsWithColon = value.endsWith(":");
        if (startsWithColon && endsWithColon) {
            return "center";
        }
        if (endsWithColon) {
            return "right";
        }
        if (startsWithColon) {
            return "left";
        }
        return null;
    });
}

function splitTableRow(line: string): string[] {
    const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
    return trimmed.split("|").map((cell) => cell.trim());
}

function renderTable(
    headerLine: string,
    alignmentRow: TableAlignment[],
    bodyLines: string[],
): string {
    const headers = splitTableRow(headerLine);
    const headerHtml = headers
        .map((cell, index): string => {
            const alignment = alignmentRow[index] ?? null;
            const style =
                alignment === null ? "" : ` style="text-align:${alignment}"`;
            return `<th${style}>${renderInline(cell)}</th>`;
        })
        .join("");

    const bodyHtml = bodyLines
        .map((bodyLine): string => {
            const cells = splitTableRow(bodyLine);
            const cellsHtml = cells
                .map((cell, index): string => {
                    const alignment = alignmentRow[index] ?? null;
                    const style =
                        alignment === null
                            ? ""
                            : ` style="text-align:${alignment}"`;
                    return `<td${style}>${renderInline(cell)}</td>`;
                })
                .join("");
            return `<tr>${cellsHtml}</tr>`;
        })
        .join("");

    return `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`;
}

// A table only renders once its alignment row is there, so while the answer streams its header
// would show as a raw line of pipes. An unfinished table header is left out until then.
const unfinishedTableHeader: RemendHandler = {
    name: "unfinishedTableHeader",
    handle: (text: string): string => {
        const lines = text.replace(/\n$/, "").split("\n");
        const last = lines.length - 1;
        const isTableLine = (index: number): boolean =>
            (lines[index] ?? "").trimStart().startsWith("|");

        if (isTableLine(last) && !isTableLine(last - 1)) {
            return lines.slice(0, last).join("\n");
        }
        if (
            /^\s*\|[\s:|-]*$/.test(lines[last]) &&
            isTableLine(last - 1) &&
            !isTableLine(last - 2)
        ) {
            return lines.slice(0, last - 1).join("\n");
        }
        return text;
    },
};

// Only the completions this renderer can display. The others insert backslashes or `~~`, which it
// would show literally.
const STREAMING_COMPLETIONS: RemendOptions = {
    linkMode: "text-only",
    singleTilde: false,
    comparisonOperators: false,
    strikethrough: false,
    katex: false,
    htmlTags: false,
    setextHeadings: false,
    handlers: [unfinishedTableHeader],
};

export function UseMarkdownRenderer(): {
    renderMarkdown(source: string): string;
    renderStreamingMarkdown(source: string): string;
} {
    function renderMarkdown(source: string): string {
        if (source === "") {
            return "";
        }

        const escaped = escapeHtml(source);
        const lines = escaped.split("\n");
        const blocks: string[] = [];

        let paragraphLines: string[] = [];
        let rootList: ListContext | null = null;
        let activeList: ListContext | null = null;
        let blockquoteLines: string[] = [];
        let codeBlockFence: string | null = null;
        let codeBlockLines: string[] = [];

        function flushParagraph(): void {
            if (paragraphLines.length === 0) {
                return;
            }
            const html = paragraphLines
                .map((line) => renderInline(line))
                .join("<br>");
            blocks.push(`<p>${html}</p>`);
            paragraphLines = [];
        }

        function flushList(): void {
            if (rootList === null) {
                return;
            }
            blocks.push(renderListItems(rootList));
            rootList = null;
            activeList = null;
        }

        function flushBlockquote(): void {
            if (blockquoteLines.length === 0) {
                return;
            }
            const inner = blockquoteLines
                .map((line) => renderInline(line))
                .join("<br>");
            blocks.push(`<blockquote>${inner}</blockquote>`);
            blockquoteLines = [];
        }

        function flushAllOpen(): void {
            flushParagraph();
            flushList();
            flushBlockquote();
        }

        for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
            const line = lines[lineIndex];

            if (codeBlockFence !== null) {
                if (isFenceClose(line, codeBlockFence)) {
                    blocks.push(
                        `<pre><code>${codeBlockLines.join("\n")}</code></pre>`,
                    );
                    codeBlockLines = [];
                    codeBlockFence = null;
                    continue;
                }
                codeBlockLines.push(line);
                continue;
            }

            const fenceOpen = detectFenceOpen(line);
            if (fenceOpen !== null) {
                flushAllOpen();
                codeBlockFence = fenceOpen.fence;
                continue;
            }

            if (line.trim() === "") {
                // Per CommonMark, a blank line ends a paragraph/blockquote but
                // does NOT end a list — `1. a\n\n1. b` is one logical list.
                flushParagraph();
                flushBlockquote();
                continue;
            }

            if (isHorizontalRule(line)) {
                flushAllOpen();
                blocks.push("<hr>");
                continue;
            }

            const heading = detectHeading(line.trim());
            if (heading !== null) {
                flushAllOpen();
                blocks.push(
                    `<h${heading.level}>${renderInline(heading.content)}</h${heading.level}>`,
                );
                continue;
            }

            if (line.includes("|") && lineIndex + 1 < lines.length) {
                const alignmentRow = parseAlignmentRow(lines[lineIndex + 1]);
                if (alignmentRow !== null) {
                    flushAllOpen();
                    const headerLine = line;
                    const bodyLines: string[] = [];
                    let bodyCursor = lineIndex + 2;
                    while (bodyCursor < lines.length) {
                        const bodyLine = lines[bodyCursor];
                        if (bodyLine.trim() === "" || !bodyLine.includes("|")) {
                            break;
                        }
                        bodyLines.push(bodyLine);
                        bodyCursor += 1;
                    }
                    blocks.push(
                        renderTable(headerLine, alignmentRow, bodyLines),
                    );
                    lineIndex = bodyCursor - 1;
                    continue;
                }
            }

            // Blockquote
            const blockquoteMatch = line.match(/^\s*&gt;\s?(.*)$/);
            if (blockquoteMatch !== null) {
                flushParagraph();
                flushList();
                blockquoteLines.push(blockquoteMatch[1]);
                continue;
            }

            const listItem = detectListItem(line);
            if (listItem !== null) {
                flushParagraph();
                flushBlockquote();

                if (rootList === null) {
                    rootList = {
                        ordered: listItem.ordered,
                        indent: listItem.indent,
                        items: [],
                        nested: [],
                    };
                    activeList = rootList;
                }

                const currentList: ListContext = activeList ?? rootList;

                if (listItem.indent > currentList.indent) {
                    const parentItemIndex: number =
                        currentList.items.length - 1;
                    let nestedList: ListContext | null =
                        parentItemIndex >= 0
                            ? currentList.nested[parentItemIndex]
                            : null;
                    if (nestedList === null) {
                        nestedList = {
                            ordered: listItem.ordered,
                            indent: listItem.indent,
                            items: [],
                            nested: [],
                        };
                        if (parentItemIndex >= 0) {
                            currentList.nested[parentItemIndex] = nestedList;
                        }
                    }
                    nestedList.items.push(listItem.content);
                    nestedList.nested.push(null);
                    activeList = nestedList;
                    continue;
                }

                const targetList: ListContext =
                    listItem.indent < currentList.indent
                        ? rootList
                        : currentList;
                targetList.items.push(listItem.content);
                targetList.nested.push(null);
                activeList = targetList;
                continue;
            }

            // Continuation line for the active list item.
            if (
                activeList !== null &&
                activeList.items.length > 0 &&
                /^\s{2,}\S/.test(line)
            ) {
                const activeContinuation: ListContext = activeList;
                const continuationIndex: number =
                    activeContinuation.items.length - 1;
                activeContinuation.items[continuationIndex] =
                    `${activeContinuation.items[continuationIndex]} ${line.trim()}`;
                continue;
            }

            flushList();
            flushBlockquote();
            paragraphLines.push(line.trim());
        }

        // Close an unterminated code fence by emitting what we have so far.
        if (codeBlockFence !== null) {
            blocks.push(`<pre><code>${codeBlockLines.join("\n")}</code></pre>`);
        }

        flushAllOpen();

        return blocks.join("");
    }

    function renderStreamingMarkdown(source: string): string {
        return renderMarkdown(remend(source, STREAMING_COMPLETIONS));
    }

    return { renderMarkdown, renderStreamingMarkdown };
}
